import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeletons";
import { parseCSV, mapCustomers } from "@/lib/csv";
import { Plus, Search, Users, Phone, Mail, MapPin, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { q: search } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length < 2) { toast.error("CSV needs a header row and at least one row of data."); return; }
    const mapped = mapCustomers(rows);
    if (mapped.columns.name < 0) { toast.error("Couldn't find a 'name' column. First column should be the customer's name."); return; }
    if (mapped.items.length === 0) { toast.error("No valid rows found."); return; }
    setCsvPreview(mapped);
    setCsvOpen(true);
  };

  const doImport = async () => {
    if (!csvPreview) return;
    setCsvImporting(true);
    try {
      const { data } = await api.post("/customers/bulk", { customers: csvPreview.items });
      toast.success(`Imported ${data.inserted} customer${data.inserted === 1 ? "" : "s"}`);
      setCsvOpen(false); setCsvPreview(null);
      await load(q);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Import failed");
    } finally { setCsvImporting(false); }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/customers", form);
      setItems((arr) => [data, ...arr]);
      setForm({ name: "", phone: "", email: "", address: "" });
      setOpen(false);
      setParams({});
      toast.success("Customer added");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to add");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Customers</h1>
        <div className="flex items-center gap-2">
          <label className="h-12 px-3 flex items-center gap-1.5 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm cursor-pointer" data-testid="csv-import-label">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import CSV</span>
            <span className="sm:hidden">CSV</span>
            <input type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" data-testid="csv-input" />
          </label>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setParams({}); }}>
          <DialogTrigger asChild>
            <Button className="h-12 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="add-customer-btn">
              <Plus className="w-5 h-5 mr-1.5" />
              <span className="hidden sm:inline">Add Customer</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle>New Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Full Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 mt-1.5" data-testid="customer-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 mt-1.5" data-testid="customer-phone" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-1.5" data-testid="customer-email" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-12 mt-1.5" data-testid="customer-address" />
              </div>
              <Button type="submit" disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="customer-save">
                {saving ? "Saving…" : "Save Customer"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) setCsvPreview(null); }}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Customers</DialogTitle>
          </DialogHeader>
          {csvPreview && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Ready to import <b>{csvPreview.items.length}</b> customer{csvPreview.items.length === 1 ? "" : "s"}
                {csvPreview.skipped > 0 && <span className="text-slate-500"> · {csvPreview.skipped} row{csvPreview.skipped === 1 ? "" : "s"} skipped (missing name)</span>}
              </div>
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="csv-preview">
                {csvPreview.items.slice(0, 20).map((c, i) => (
                  <div key={i} className="p-2.5 text-sm">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {[c.phone, c.email, c.address].filter(Boolean).join(" · ") || <span className="opacity-50">no other fields</span>}
                    </div>
                  </div>
                ))}
                {csvPreview.items.length > 20 && (
                  <div className="p-2 text-xs text-slate-500 text-center">…and {csvPreview.items.length - 20} more</div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Expected columns: <b>name</b> (required), <b>phone</b>, <b>email</b>, <b>address</b>. First row must be headers.
              </p>
              <Button onClick={doImport} disabled={csvImporting} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="csv-import-confirm">
                {csvImporting ? "Importing…" : `Import ${csvPreview.items.length}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone or email…"
          className="h-12 pl-10 bg-white"
          data-testid="customer-search"
        />
      </div>

      {loading ? (
        <CardSkeleton count={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No customers yet"
          description="Add your first customer to start tracking their plumbing work."
          action={
            <Button onClick={() => setOpen(true)} className="h-12 px-5 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl">
              <Plus className="w-5 h-5 mr-1.5" /> Add Customer
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <Link key={c.id} to={`/customers/${c.id}`} data-testid={`customer-row-${c.id}`}>
              <div className="bg-white rounded-2xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all active:scale-[0.99] mb-2">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                      {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</span>}
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.email}</span>}
                      {c.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{c.address}</span>}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
